import React, { useEffect, useState, useCallback } from 'react';
import { animated } from '@react-spring/web';

import AutocompleteAddressInput from '~/components/inputs/autocomplete-address-input/autocomplete-address-input';
import Button from '~/components/button';
import { AutocompleteTypes } from '~/components/inputs/autocomplete-address-input/autocomplete-storage';
import LoadingIndicator from '~/components/loading-indicator';
import Modal from '~/components/modals/modal';
import SignInModalForm from '~/components/modals/funnel-auth/sign-in-modal-form';
import RegisterModalForm, {
  REGISTRATION_SOURCES,
} from '~/components/modals/funnel-auth/register-modal-form';
import TermsOfUseLinks from '~/components/terms-and-conditions/terms-of-use-links';
import MagicLinkModalForm from '~/components/modals/funnel-auth/magic-link-modal-form';
import { api } from '~/api';
import { useLawnMutations } from '~/features/lawns/lawn-actions';
import { useSession } from '~/features/session/session-actions';
import { useModal } from '~/features/modals/modal-actions';
import { useAnimatedPageTransitions } from '~/hooks/use-animated-page-transitions';
import { validateAddress } from '~/components/lawns/lawn-autocomplete-form';
import { analyticsBeacon, DeprecatedEventType } from '~/utils/analytics';
import { captureException } from '~/utils/exception-tracking';

import modalStyles from './store-auth-modal.module.scss';
import styles from '~/components/modals/modal.module.scss';

export const Page = {
  CREATE_LAWN: 0,
  REGISTER: 1,
  SIGN_IN: 2,
  LOADING: 3,
  MAGIC_LINK: 4,
};

export const AuthType = {
  REGISTER: 'register',
  SIGN_IN: 'sign in',
};

const StoreAuthModal = ({
  isOpen,
  magicLinkRedirectUrl,
  onAuth = () => {},
  onClose,
  userAddress,
  type = AutocompleteTypes.FULL_ADDRESS,
}) => {
  const { transitions, transitionTo, resetPage } = useAnimatedPageTransitions(
    Page.CREATE_LAWN
  );
  const { claimLawn, createLawn } = useLawnMutations();
  const { showModal } = useModal();
  const { user } = useSession();

  const [email, setEmail] = useState();
  const [address, setAddress] = useState(null);
  const [lawnUuid, setLawnUuid] = useState(null);
  const [sentMagicLink, setSentMagicLink] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const errorMessage = address && validateAddress(address);
  const isButtonDisabled = !address;

  const handleCloseModal = useCallback(() => {
    resetPage();
    setAddress(null);
    setEmail(null);
    setSentMagicLink(false);
    onClose();
  }, [onClose, resetPage]);

  const handleConfirmLawn = useCallback(
    async (uuid) => {
      if (user) {
        await claimLawn(uuid);
        onAuth();
        handleCloseModal();
      } else {
        transitionTo(Page.REGISTER, 1);
      }
    },
    [claimLawn, handleCloseModal, onAuth, transitionTo, user]
  );

  useEffect(() => {
    if (isOpen && userAddress) {
      setAddress(userAddress);
      createLawn(userAddress).then(({ address, uuid }) => {
        setAddress(address);
        setLawnUuid(uuid);
        handleConfirmLawn(uuid);
      });
    }
  }, [createLawn, handleConfirmLawn, isOpen, transitionTo, userAddress]);

  const handleAddressContinue = () => {
    setLoading(true);

    if (errorMessage) {
      setSubmitFailed(true);
      setLoading(false);
      return;
    }

    createLawn(address).then(({ address, uuid }) => {
      setAddress(address);
      setLawnUuid(uuid);
      setLoading(false);
      handleConfirmLawn(uuid);
    });
  };

  const handleRequestMagicLink = (email) => {
    return api.sessions
      .requestMagicLink({
        email,
        queryParams: { redirect: magicLinkRedirectUrl },
      })
      .then(() => {
        setEmail(email);
        setSentMagicLink(true);
      })
      .catch(() => showModal('ERROR'));
  };

  return (
    <Modal
      onClose={handleCloseModal}
      isOpen={isOpen}
      className="sunday-store-auth"
    >
      {transitions((styleProps, item) => {
        switch (item) {
          case Page.CREATE_LAWN:
            return (
              <animated.div style={styleProps} className={styles.container}>
                <h1 className={modalStyles.createTitle}>Let's do this!</h1>
                <p className={modalStyles.createSmallText}>
                  We're all about tailoring products for where you live. Share
                  your{' '}
                  {type === AutocompleteTypes.FULL_ADDRESS
                    ? 'home address'
                    : 'zip code'}{' '}
                  so we can make sure you're getting what you need.
                </p>
                <div className={modalStyles.unboxInputContainer}>
                  <AutocompleteAddressInput
                    type={type}
                    error={errorMessage}
                    submitFailed={submitFailed}
                    placeholder={
                      type === AutocompleteTypes.FULL_ADDRESS
                        ? 'Home address'
                        : 'Zip code'
                    }
                    onSelect={setAddress}
                    inputClassname={modalStyles.unboxInput}
                    label={
                      type === AutocompleteTypes.FULL_ADDRESS
                        ? 'Home address'
                        : 'Zip code'
                    }
                    containerClassname={modalStyles.unboxAutocompleteContainer}
                  />
                  <Button
                    disabled={isButtonDisabled}
                    onClick={handleAddressContinue}
                    fullWidth
                    className={modalStyles.unboxButton}
                    isLoading={loading}
                  >
                    Continue
                  </Button>
                </div>
                {!user && (
                  <p className={modalStyles.smallText}>
                    Already have an account?
                    <Button
                      variant="link"
                      className={modalStyles.smallLinkButton}
                      onClick={() => transitionTo(Page.SIGN_IN, 1)}
                    >
                      Sign in
                    </Button>
                  </p>
                )}
              </animated.div>
            );

          case Page.REGISTER:
            return (
              <animated.div style={styleProps} className={styles.container}>
                <h1 className={styles.title}>Create account</h1>
                <p className={styles.text}>
                  Enter your email address to save your account and shop with
                  personalized recommendations.
                </p>

                <RegisterModalForm
                  source={REGISTRATION_SOURCES.STORE}
                  onSuccess={async () => {
                    // Doing this here instead of the modal's open location because
                    // we open this modal in several spots, but want this event to live
                    // in all of them!
                    analyticsBeacon.emit(DeprecatedEventType.LEAD_STORE);

                    try {
                      await claimLawn(lawnUuid);
                    } catch (error) {
                      // If the user has a paid subscription, they're not allowed to claim a new lawn. Let them proceed without issue
                      // If we see another type of error, let's capture it!
                      if (
                        error?.detail !==
                        'User has paid Subscription and is unable to update their lawn.'
                      ) {
                        captureException(
                          new Error('Error claiming lawn in Store Auth Modal'),
                          {
                            extras: {
                              errorInfo: JSON.stringify(error),
                            },
                          }
                        );
                      }
                    }
                    onAuth(AuthType.REGISTER);
                    handleCloseModal();
                  }}
                  onCancel={handleCloseModal}
                  onExistingEmailError={(email) => {
                    setEmail(email);
                    transitionTo(Page.SIGN_IN, 1);
                  }}
                />
                <TermsOfUseLinks />
              </animated.div>
            );

          case Page.SIGN_IN:
            return (
              <animated.div style={styleProps} className={styles.container}>
                <h1 className={styles.title}>Sign in</h1>
                <p className={styles.text}>
                  It looks like you already have an account. Please sign in to
                  continue.
                </p>

                <SignInModalForm
                  onSuccess={() => {
                    onAuth(AuthType.SIGN_IN, { email });
                    handleCloseModal();
                  }}
                  onCancel={() => transitionTo(Page.CREATE_LAWN, -1)}
                  onRequestMagicLink={(email) => {
                    if (email) {
                      setEmail(email);
                    }
                    transitionTo(Page.MAGIC_LINK, 1);
                  }}
                  email={email}
                />
              </animated.div>
            );

          case Page.MAGIC_LINK:
            return (
              <animated.div style={styleProps} className={styles.container}>
                {sentMagicLink ? (
                  <>
                    <h1 className={styles.title}>Sign in link sent</h1>
                    <img
                      src="/icons/checkmark-circle.svg?v3"
                      alt=""
                      width="24"
                      height="24"
                      style={{
                        width: '3.2rem',
                        height: 'auto',
                        display: 'block',
                        margin: '-2.4rem auto 3.2rem auto',
                      }}
                    />
                    <p className={styles.text} style={{ textAlign: 'center' }}>
                      We're sending a sign in link to <strong>{email}</strong>.
                      <br />
                      Please allow a few minutes for the email to arrive.
                    </p>
                  </>
                ) : (
                  <>
                    <h1 className={styles.title}>Sign in link</h1>
                    <p className={styles.text} style={{ textAlign: 'center' }}>
                      Enter your email address below and we'll send you a link
                      you can use to sign in.
                    </p>
                    <MagicLinkModalForm
                      initialValues={{ email }}
                      onSubmit={({ email }) => handleRequestMagicLink(email)}
                      onCancel={() => transitionTo(Page.SIGN_IN, -1)}
                    />
                  </>
                )}
              </animated.div>
            );

          case Page.LOADING:
            return (
              <animated.div style={styleProps} className={styles.container}>
                <div style={{ padding: '4rem 4rem 8rem', textAlign: 'center' }}>
                  <h1>Loading</h1>
                  <LoadingIndicator />
                </div>
              </animated.div>
            );

          default:
            return null;
        }
      })}
    </Modal>
  );
};

export default StoreAuthModal;
